import React, { FC, useCallback, useRef } from 'react';

import { SafeAreaView, ScrollView, StyleSheet, View } from 'react-native';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import LoadingAnimation from 'components/loading-animation';
import { Table } from 'components/table';
import { useScrollToHighlightedRow } from 'pages/loyalty/transactions/use-scroll-to-highlighted-row';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';
import { isNative } from 'utils/environment';
import { ITable } from 'utils/table';

import { TransactionTable, TransactionTableRow } from './types';

interface HistoryLazyTableListProps {
  tables: TransactionTable[];
  visibleTransactions?: number;
  handleOnPressRow: (row: TransactionTableRow | Partial<TransactionTableRow>) => void;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  highlightedRowId?: string;
  scrollViewRef?: React.RefObject<ScrollView | null>;
}

export const HistoryLazyTableList: FC<HistoryLazyTableListProps> = ({
  tables,
  visibleTransactions,
  handleOnPressRow,
  loadingMore,
  onLoadMore,
  highlightedRowId,
  scrollViewRef,
}) => {
  const rowRefs = useRef<Record<string, View | null>>({});
  useScrollToHighlightedRow(scrollViewRef, rowRefs, highlightedRowId);

  const renderItem = useCallback(
    ({ item, index }: { item: ITable<Partial<TransactionTableRow>>; index: number }) => (
      <Table<TransactionTableRow>
        key={`historyTable.${index}`}
        table={item}
        onPressRow={handleOnPressRow}
        getRowId={row => row.transactionId}
        getRowProps={row => {
          const rowId = row.transactionId;
          return {
            ref: (node: View | null) => {
              if (rowId) {
                rowRefs.current[rowId] = node;
              }
            },
            backgroundColor:
              rowId && rowId === highlightedRowId ? primitive.$meltyYellowOpacity20 : undefined,
          };
        }}
        marginTop="$4"
      />
    ),
    [handleOnPressRow, highlightedRowId]
  );

  const visibleTables = React.useMemo(() => {
    if (isNative && visibleTransactions) {
      return tables.slice(0, visibleTransactions);
    }
    return tables;
  }, [tables, visibleTransactions]);

  // Lazy Loading only for Native...
  if (isNative && visibleTransactions) {
    return (
      <SafeAreaView style={styles.container}>
        {visibleTables.map((item, index) => renderItem({ item, index }))}
        {loadingMore && (
          <CenteredRowContainer>
            <View style={{ padding: spacing.$8 }}>
              <LoadingAnimation />
            </View>
          </CenteredRowContainer>
        )}
        {isNative && !loadingMore && visibleTransactions < tables.length && (
          <View style={styles.loadMoreContainer}>
            <ActionButton
              variant={ActionButtonVariants.OUTLINE_V2}
              onPress={onLoadMore}
              size={ActionButtonSizes.SMALL}
              alignSelf="center"
            >
              {t('loadMore')}
            </ActionButton>
          </View>
        )}
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {visibleTables.map((item, index) => renderItem({ item, index }))}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadMoreContainer: {
    padding: spacing.$4,
  },
});
